import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { AmplitudeEvent } from '../constants/amplitudeEvents';

type AmplitudeModule = typeof import('@amplitude/analytics-browser');

interface AmplitudeContextType {
    trackEvent: (eventName: AmplitudeEvent, eventProperties?: Record<string, any>) => void;
    setUserId: (userId: string) => void;
    setUserProperties: (properties: Record<string, any>) => void;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(undefined);

interface AmplitudeProviderProps {
    apiKey: string;
    children: ReactNode;
}

export const AmplitudeProvider: React.FC<AmplitudeProviderProps> = ({ apiKey, children }) => {
    const amplitudeRef = useRef<AmplitudeModule | null>(null);
    const eventQueueRef = useRef<Array<{ type: 'track' | 'identify' | 'setUserId'; payload: Record<string, any> }>>([]);
    const loadingRef = useRef(false);

    const loadAmplitude = () => {
        if (loadingRef.current || amplitudeRef.current) return;
        loadingRef.current = true;

        import('@amplitude/analytics-browser').then((amp) => {
            amp.init(apiKey, undefined, {
                defaultTracking: {
                    pageViews: true,
                    sessions: true,
                    formInteractions: false,
                    fileDownloads: false,
                },
            });
            amplitudeRef.current = amp;

            // Set hostname as a user property on first load
            const identify = new amp.Identify();
            identify.setOnce('source_domain', window.location.hostname);
            amp.identify(identify);

            // Flush queued events
            eventQueueRef.current.forEach(({ type, payload }) => {
                if (type === 'track') {
                    amp.track(payload.eventName, payload.eventProperties);
                } else if (type === 'setUserId') {
                    const identify = new amp.Identify();
                    identify.set('user_id', payload.userId);
                    identify.setOnce('first_identified_at', new Date().toISOString());
                    amp.identify(identify);
                    amp.flush();
                } else if (type === 'identify') {
                    const identify = new amp.Identify();
                    Object.entries(payload.properties).forEach(([key, value]) => {
                        identify.set(key, value as any);
                    });
                    amp.identify(identify);
                }
            });
            eventQueueRef.current = [];
        });
    };

    useEffect(() => {
        // Delay Amplitude loading by 3s or until first user interaction
        const timer = setTimeout(loadAmplitude, 3000);
        const events = ['mousedown', 'keydown', 'touchstart', 'scroll'] as const;
        const onInteraction = () => {
            clearTimeout(timer);
            loadAmplitude();
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
        events.forEach(e => document.addEventListener(e, onInteraction, { once: true, passive: true }));

        return () => {
            clearTimeout(timer);
            events.forEach(e => document.removeEventListener(e, onInteraction));
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey]);

    const trackEvent = (eventName: AmplitudeEvent, eventProperties?: Record<string, any>) => {
        console.log(eventName);
        if (amplitudeRef.current) {
            amplitudeRef.current.track(eventName, eventProperties);
        } else {
            eventQueueRef.current.push({ type: 'track', payload: { eventName, eventProperties } });
        }
    };

    const setUserId = (userId: string) => {
        if (amplitudeRef.current) {
            const amp = amplitudeRef.current;
            const identify = new amp.Identify();
            identify.set('user_id', userId);
            identify.setOnce('first_identified_at', new Date().toISOString());
            amp.identify(identify);
            amp.flush();
        } else {
            eventQueueRef.current.push({ type: 'setUserId', payload: { userId } });
        }
    };

    const setUserProperties = (properties: Record<string, any>) => {
        if (amplitudeRef.current) {
            const amp = amplitudeRef.current;
            const identify = new amp.Identify();
            Object.entries(properties).forEach(([key, value]) => {
                identify.set(key, value);
            });
            amp.identify(identify);
        } else {
            eventQueueRef.current.push({ type: 'identify', payload: { properties } });
        }
    };

    return (
        <AmplitudeContext.Provider value={{ trackEvent, setUserId, setUserProperties }}>
            {children}
        </AmplitudeContext.Provider>
    );
};

// Custom hook to use Amplitude
export const useAmplitude = (): AmplitudeContextType => {
    const context = useContext(AmplitudeContext);
    if (!context) {
        throw new Error('useAmplitude must be used within AmplitudeProvider');
    }
    return context;
};

// Hook to fire events once on mount (prevents double-fire in React StrictMode)
export const useTrackOnMount = (
    events: Array<{ event: AmplitudeEvent; properties?: Record<string, any> }>
) => {
    const { trackEvent } = useAmplitude();
    const hasFired = useRef(false);

    useEffect(() => {
        if (hasFired.current) return;
        hasFired.current = true;
        events.forEach(({ event, properties }) => {
            trackEvent(event, properties);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};
